import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import TaskDetailModal from '../TaskDetailModal';
import enhancedApiClient from '../../api/enhancedApiClient';

const VendorEventDetailModal = ({ event, onClose }) => {
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [venueName, setVenueName] = useState('');
  const [threads, setThreads] = useState([]);
  const [eventVendors, setEventVendors] = useState([]);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    const loadEventData = async () => {
      const eventIdToUse = event?.eventId || event?.id;
      if (!eventIdToUse) {
        console.log('No eventId found in event:', event);
        return;
      }
      
      console.log('Loading data for eventId:', eventIdToUse);
      
      try {
        const [tasksData, guestsData] = await Promise.all([
          enhancedApiClient.getTasks(eventIdToUse),
          fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventIdToUse}`).then(r => r.json())
        ]);
        
        setTasks(tasksData || []);
        setGuests(guestsData || []);
        
        // Load threads
        const threadsData = await enhancedApiClient.getThreads(eventIdToUse, currentUser?.username);
        setThreads(threadsData || []);
        
        // Load venue name
        const vendors = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventIdToUse}/vendors`).then(r => r.json());
        const venue = vendors.find(v => v.vendorType === 'venue');
        if (venue) setVenueName(venue.name);

        // Load all vendor details for contact info
        const vendorDetails = [];
        for (const assoc of vendors) {
          if (!assoc.vendorId || assoc.vendorId === 'undefined') continue;
          try {
            const vRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${assoc.vendorId}`);
            if (vRes.ok) {
              const vData = await vRes.json();
              const v = {};
              for (const [key, value] of Object.entries(vData)) {
                if (value?.S !== undefined) v[key] = value.S;
                else if (value?.N !== undefined) v[key] = parseInt(value.N);
                else v[key] = value;
              }
              vendorDetails.push(v);
            }
          } catch {}
        }
        setEventVendors(vendorDetails);
        
        // Auto-select first incomplete task
        const incompleteTasks = tasksData.filter(t => t.status !== 'completed');
        if (incompleteTasks.length > 0) {
          setSelectedTask(incompleteTasks[0]);
        } else if (tasksData.length > 0) {
          setSelectedTask(tasksData[0]);
        }
      } catch (error) {
        console.error('Error loading event data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadEventData();
  }, [event?.eventId, event?.id, currentUser?.username]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#E8F4E8',
          padding: '32px 24px',
          textAlign: 'center',
          position: 'relative'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6B7280'
            }}
          >
            ×
          </button>
          <div style={{ fontSize: '16px', color: '#6B7280', marginBottom: '8px' }}>
            {event?.eventDate ? new Date(event.eventDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : ''} · {venueName}
          </div>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '400',
            color: '#1F2937',
            margin: 0,
            fontFamily: 'Georgia, serif'
          }}>
            {event?.name || 'Event'}
          </h2>
          
          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            marginTop: '24px'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '16px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1F2937' }}>
                {event?.status === 'confirmed' ? 'Confirmed' : 'Pending'}
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              padding: '16px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1F2937' }}>
                ${event?.budget ? (event.budget / 1000).toFixed(0) + 'k' : '0'} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>total value</span>
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              padding: '16px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1F2937' }}>
                {guests.length} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>estimated guests</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
            {/* Checklist Sidebar */}
            <div style={{ backgroundColor: '#F9FAFB', borderRadius: '12px', padding: '24px', height: 'fit-content' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '20px', fontFamily: 'Georgia, serif' }}>
                Checklist
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tasks.slice(0, 5).map(task => (
                  <div 
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: selectedTask?.id === task.id ? 'white' : 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: selectedTask?.id === task.id ? '1px solid #6B9080' : '1px solid transparent'
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#E8F4E8',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                        <path d="M9 11l3 3L22 4"></path>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                      </svg>
                      {task.status === 'completed' && (
                        <div style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          width: '16px',
                          height: '16px',
                          backgroundColor: '#10B981',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <polyline points="20,6 9,17 4,12"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#1F2937', fontSize: '14px' }}>{task.name}</div>
                      <div style={{ color: '#6B7280', fontSize: '12px' }}>{task.dueDate ? new Date(task.dueDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : ''}</div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                      <polyline points="9,18 15,12 9,6"/>
                    </svg>
                  </div>
                ))}
              </div>
              
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button 
                  onClick={() => {
                    onClose();
                    navigate('/vendor/checklist');
                  }}
                  style={{
                    color: '#6B9080',
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                >
                  View or edit checklist
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Task Detail */}
            {selectedTask && (
              <div style={{ backgroundColor: '#E8F4E8', borderRadius: '12px', padding: '32px' }}>
                <h2 style={{ fontSize: '32px', fontWeight: '600', color: '#1F2937', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                  {selectedTask.name}
                </h2>
                <div style={{ color: '#6B7280', fontSize: '16px', marginBottom: '24px' }}>
                  {selectedTask.status === 'completed' ? 'Completed' : 'Awaiting client acceptance'}
                </div>
                
                <p style={{ color: '#4B5563', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
                  {selectedTask.details || 'No additional details provided.'}
                </p>

                {/* Document */}
                {selectedTask.documentUrl && (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#6B9080',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '16px', color: '#1F2937', marginBottom: '4px' }}>
                        {selectedTask.documentUrl.split('/').pop().split('-').slice(1).join('-') || 'Document'}
                      </div>
                      <div style={{ color: '#6B7280', fontSize: '14px' }}>
                        Uploaded {selectedTask.createdAt ? new Date(selectedTask.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : ''}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button 
                    variant="primary"
                    onClick={() => setShowDetailView(true)}
                  >
                    View Details
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      const existingThread = threads.find(t => t.taskId === selectedTask.id);
                      if (existingThread) {
                        onClose();
                        navigate('/vendor/messages', { state: { selectedThreadId: existingThread.threadId } });
                      } else {
                        onClose();
                        navigate('/vendor/messages');
                      }
                    }}
                  >
                    Open Messages
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vendors at this Event */}
      {eventVendors.length > 0 && (
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '12px' }}>Vendors at this Event</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {eventVendors.map((v, i) => (
              <div key={i} style={{ padding: '14px 16px', backgroundColor: '#F9FAFB', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#E8F4E8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', color: '#6B9080', flexShrink: 0 }}>
                  {(v.name || '?').substring(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#1F2937' }}>{v.name || 'Unknown'}</div>
                  <div style={{ fontSize: '13px', color: '#6B7280' }}>{v.vendorType || 'Vendor'}</div>
                </div>
                {v.email && <a href={`mailto:${v.email}`} style={{ fontSize: '13px', color: '#6B9080', textDecoration: 'none' }}>{v.email}</a>}
                {v.phone && <span style={{ fontSize: '13px', color: '#6B7280' }}>{v.phone}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      
      <TaskDetailModal
        task={showDetailView ? selectedTask : null}
        onClose={() => setShowDetailView(false)}
        eventId={event?.eventId || event?.id}
        currentUser={currentUser}
        onStatusUpdate={() => {
          setShowDetailView(false);
        }}
      />
    </div>
  );
};

export default VendorEventDetailModal;
